import { useEffect } from 'react'
import { setMetaTags } from '@/utils/seo'
import { Link } from 'react-router-dom'
import { CheckCircle, ChevronRight } from 'lucide-react'
import SiluxPromise from '@/components/SiluxPromise'

const SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "name": "Broadband for Renters UK — No Landline, No Engineer",
      "provider": { "@type": "Organization", "name": "Silux Mobile", "url": "https://www.siluxmobile.co.uk" },
      "serviceType": "Mobile Broadband",
      "areaServed": "United Kingdom",
      "description": "Mobile broadband for renters who can't get a landline installed. No engineer visit, no landlord permission, router included, and you choose a plan from 1 month (rolling monthly, 30 days’ notice) to 24 months. Works in flats and rented properties and moves with you when you move. Start the day your router arrives. No price rises during your contract and free broadband upgrades.",
      "url": "https://www.siluxmobile.co.uk/broadband-renters"
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "Can I get broadband in a rented flat without a landline?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Mobile broadband from Silux Mobile requires no landline, no BT Openreach engineer, and no landlord permission. You simply insert a SIM card into a 4G/5G router and connect your devices. Perfect for renters." } },
        { "@type": "Question", "name": "Do I need my landlord's permission to get broadband?", "acceptedAnswer": { "@type": "Answer", "text": "For traditional fibre broadband, yes — an engineer often needs to drill holes or install a socket. With Silux Mobile's mobile broadband, there's nothing to install. No permission needed." } },
        { "@type": "Question", "name": "What if I move flat — can I take my broadband with me?", "acceptedAnswer": { "@type": "Answer", "text": "Yes — that's one of the biggest advantages of mobile broadband for renters. Your SIM and router simply come with you and work at any UK address with Three, O2 or Vodafone coverage. Nothing to reinstall, no waiting for a new line, and no need to start a new plan." } },
        { "@type": "Question", "name": "How long do I have to commit for?", "acceptedAnswer": { "@type": "Answer", "text": "You choose the term when you order, from 1 month: a rolling monthly contract from £25.95/month (1-month minimum term, 30 days’ notice to leave, £30 refundable holding deposit), or a fixed 3, 6, 12, 18 or 24 months. Broadband Lite is £15.95 a month over 24 months, and 3-month plans start at £19.95 a month plus a £15 refundable holding deposit. There is no setup fee on any plan, and because the broadband is portable, moving house does not mean starting again." } },
        { "@type": "Question", "name": "Is mobile broadband good enough for a flat?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. Most UK flats have excellent 4G or 5G coverage. Silux Mobile's unlimited plans deliver speeds of 10–400 Mbps depending on your plan and coverage (10–50 Mbps on Broadband Lite, 50–150 Mbps on 5G Premium over 4G, 80–400 Mbps on 5G) — enough for multiple devices, Netflix, gaming and working from home." } },
        { "@type": "Question", "name": "How quickly can I get broadband in my rented flat?", "acceptedAnswer": { "@type": "Answer", "text": "Delivery is free by Royal Mail Tracked 48 (2–3 working days), or choose faster delivery at checkout. Insert the SIM into your router. You have broadband the same day your SIM arrives — no waiting weeks for an engineer." } },
        { "@type": "Question", "name": "Will my broadband price increase during my contract?", "acceptedAnswer": { "@type": "Answer", "text": "No. Your price is fixed for the life of your plan — there are no price increases during your contract and no annual inflation-linked price rises. What you sign up at is what you pay." } },
        { "@type": "Question", "name": "Are broadband upgrades free?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Silux Mobile offers free broadband upgrades — move to a faster plan or upgrade your router with no upgrade fee. Free upgrades are included as standard." } }
      ]
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.siluxmobile.co.uk" },
        { "@type": "ListItem", "position": 2, "name": "Broadband", "item": "https://www.siluxmobile.co.uk/broadband" },
        { "@type": "ListItem", "position": 3, "name": "Broadband for Renters", "item": "https://www.siluxmobile.co.uk/broadband-renters" }
      ]
    }
  ]
}

const VS = [
  { aspect: "Landlord permission", fibre: "Often required", mobile: "Never needed", win: "mobile" },
  { aspect: "Engineer visit", fibre: "Yes — weeks wait", mobile: "No", win: "mobile" },
  { aspect: "Contract length", fibre: "12–24 months", mobile: "3–24 months, your choice", win: "mobile" },
  { aspect: "Setup cost", fibre: "£0–£100", mobile: "£0", win: "mobile" },
  { aspect: "Portable on moving day", fibre: "No — cancel and restart", mobile: "Yes — same SIM, same plan", win: "mobile" },
  { aspect: "Typical monthly cost", fibre: "£25–45", mobile: "£15.95–£32.95", win: "mobile" },
  { aspect: "Speed", fibre: "Up to 900 Mbps (FTTP)", mobile: "10–400 Mbps", win: "fibre" },
]

export default function BroadbandRenters() {
  useEffect(() => {
    setMetaTags({
      title: 'Broadband for Renters UK | No Landline, Moves With You | Silux Mobile',
      description: "Broadband for renters and tenants — no landline, no engineer and no landlord permission needed. 4G & 5G mobile broadband from £15.95/month with the router included, and it moves with you when you move. Plans from 1 month (rolling) to 24 months.",
      keywords: ['broadband for renters', 'broadband for tenants', 'broadband without landline', 'flexible broadband UK', 'short term broadband', 'movable broadband', 'rented flat broadband', 'mobile broadband UK'],
      canonical: 'https://www.siluxmobile.co.uk/broadband-renters',
    })
    const s = document.createElement('script')
    s.type = 'application/ld+json'
    s.text = JSON.stringify(SCHEMA)
    document.head.appendChild(s)
    return () => { document.head.removeChild(s) }
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-700 to-blue-900 text-white py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <nav className="text-blue-300 text-sm mb-6 flex items-center gap-2">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/broadband" className="hover:text-white">Broadband</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Broadband for Renters</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Broadband for Renters UK</h1>
          <p className="text-xl text-blue-100 mb-3 max-w-2xl">
            No landline. No engineer. No landlord permission. Get broadband in your rented flat or house as soon as your router lands — and take it with you when you move.
          </p>
          <p className="text-blue-200 text-base mb-8 max-w-2xl">
            Over 4.6 million households in the UK rent privately. Most are locked out of the cheapest fibre deals because they can't get an engineer to drill into someone else's property. <strong>Mobile broadband for renters</strong> solves that — it's a SIM in a router we send you, with nothing to install, nothing to make good when you leave, and a plan length you choose yourself, from a 1-month rolling contract to 24 months.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/broadband" className="bg-white text-blue-800 font-semibold px-6 py-3 rounded-lg hover:bg-blue-50 transition">
              See Renter Broadband Plans
            </Link>
            <Link to="/network-coverage" className="border border-white text-white px-6 py-3 rounded-lg hover:bg-white/10 transition">
              Check Flat Coverage
            </Link>
          </div>
        </div>
      </section>

      <SiluxPromise variant="broadband" />

      {/* How it works */}
      <section className="py-16 px-4 bg-blue-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">How It Works — 3 Steps</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: "1", title: "Order Online", body: "Choose your plan and your term. Router and SIM delivered free by Royal Mail Tracked 48 (2–3 working days). No credit check for most plans." },
              { step: "2", title: "Insert SIM into Router", body: "Put the SIM into the router we send you — or any unlocked 4G/5G router or MiFi you already own. No tools, no wiring." },
              { step: "3", title: "Connect Everything", body: "Your router creates a WiFi network. Connect phones, laptops, TV — done in minutes." },
            ].map(s => (
              <div key={s.step} className="bg-white rounded-xl p-6 shadow-sm text-center">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">{s.step}</div>
                <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Mobile vs Fibre — For Renters</h2>
          <p className="text-gray-500 text-center mb-8">Why mobile broadband wins almost every category for renters.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
              <thead className="bg-gray-900 text-white">
                <tr>
                  <th className="px-4 py-3 text-left">Consideration</th>
                  <th className="px-4 py-3 text-center">Fibre Broadband</th>
                  <th className="px-4 py-3 text-center">Silux Mobile Broadband</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {VS.map(row => (
                  <tr key={row.aspect} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{row.aspect}</td>
                    <td className={`px-4 py-3 text-center text-sm ${row.win === 'fibre' ? 'text-green-700 font-medium' : 'text-red-500'}`}>{row.fibre}</td>
                    <td className={`px-4 py-3 text-center text-sm ${row.win === 'mobile' ? 'text-green-700 font-semibold' : 'text-gray-500'}`}>{row.mobile}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-gray-500 text-xs text-center mt-4">
            There is no setup fee on any Silux Mobile broadband plan. Short plans take a refundable holding deposit (£30 on rolling monthly Lite, £15 on 3-month plans), which comes back to you.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Renter-Friendly Broadband Plans</h2>
          <p className="text-gray-500 text-center mb-10">Pick a short plan if you're not sure how long you'll stay, or a longer one for the best monthly price. Either way it moves with you.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { name: "Rolling Monthly", price: "£25.95", features: ["1-month minimum, then month to month", "30 days’ notice to leave", "4G router included", "£30 refundable holding deposit", "Free delivery"], color: "border-indigo-200" },
              { name: "3-Month Flex", price: "£19.95", features: ["3-month plan", "4G/5G router included", "£15 refundable holding deposit", "Takes 10 minutes to set up", "Free delivery"], color: "border-blue-200" },
              { name: "Broadband Lite", price: "£15.95", features: ["24-month plan — our best rate", "Unlimited data on 4G/5G", "4G/5G router included", "Moves with you when you move", "Free delivery"], color: "border-blue-500", popular: true },
            ].map(p => (
              <div key={p.name} className={`bg-white rounded-2xl p-6 border-2 ${p.color} relative ${p.popular ? 'shadow-lg' : ''}`}>
                {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR FOR RENTERS</div>}
                <h3 className="text-2xl font-bold mb-1">{p.name}</h3>
                <p className="text-4xl font-bold text-blue-700 mb-4">{p.price}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                <ul className="space-y-2 mb-6">
                  {p.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/broadband" className={`block text-center py-3 rounded-lg font-semibold text-sm transition ${p.popular ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
                  Get This Plan
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Renter Broadband FAQs</h2>
          <div className="space-y-4">
            {(SCHEMA["@graph"][1] as any).mainEntity.map((q: any) => (
              <details key={q.name} className="bg-gray-50 rounded-xl border border-gray-200 p-5 group">
                <summary className="font-semibold text-gray-900 cursor-pointer list-none flex justify-between items-center">
                  {q.name}
                  <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="text-gray-600 text-sm mt-3">{q.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-12 px-4 bg-blue-800 text-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold mb-4 text-center">More from Silux Mobile</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { to: "/mobile-broadband", label: "Mobile Broadband" },
              { to: "/rolling-monthly-broadband", label: "Rolling Monthly Broadband" },
              { to: "/cheap-broadband", label: "Cheap Broadband" },
              { to: "/no-contract-sim", label: "No Contract SIM" },
              { to: "/broadband-rural-uk", label: "Rural Broadband" },
            ].map(l => (
              <Link key={l.to} to={l.to} className="bg-white/10 hover:bg-white/20 rounded-lg px-4 py-3 text-center text-sm font-medium transition">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
